// composables/useMenu.ts
import { ref, onMounted, onBeforeUnmount, watch, type Ref } from 'vue'

const isClient = import.meta.client

export function useMenu(
    visible: Ref<boolean>,
    menu: Ref<HTMLElement | null>,
    useClickFirst: boolean,
    persistent: boolean = false
): {
    clickFirst?: Ref<boolean>
    addEvent: () => void
    closeDropdownByClickOutside: (e: MouseEvent) => void
} {
    const clickFirst = useClickFirst ? ref(false) : undefined

    const closeDropdownByClickOutside = (e: MouseEvent) => {
        if (persistent) return

        if (isClient) {
            const sel = typeof getSelection === 'function' ? getSelection() : null
            if (sel && sel.toString().trim() !== '') return
        }

        if (
            menu.value &&
            !menu.value.contains(e.target as Node) &&
            (useClickFirst ? (clickFirst?.value ?? true) : true)
        ) {
            visible.value = false
        }

        if (useClickFirst && clickFirst && !clickFirst.value) {
            clickFirst.value = true
        }
    }

    const addEvent = () => {
        if (isClient) window.addEventListener('click', closeDropdownByClickOutside)
    }
    const removeEvent = () => {
        if (isClient) window.removeEventListener('click', closeDropdownByClickOutside)
    }

    let stopWatch: (() => void) | undefined

    onMounted(() => {
        // стартуем реакцию только в браузере после mount
        stopWatch = watch(
            visible,
            (v) => {
                if (v) addEvent()
                else removeEvent()
            },
            { immediate: true }
        )
    })

    onBeforeUnmount(() => {
        removeEvent()
        stopWatch?.()
    })

    return { clickFirst, addEvent, closeDropdownByClickOutside }
}

export function useMenuWithRef(
    visible: Ref<boolean>,
    menu: Ref<HTMLElement | null>
): {
    clickFirst: Ref<boolean>
    changeState: () => void
    open: () => void
    close: () => void
    closeDropdownByClickOutside: (e: MouseEvent) => void
} {
    const clickFirst = ref(false)

    const closeDropdownByClickOutside = (e: MouseEvent) => {
        if (isClient) {
            const sel = typeof getSelection === 'function' ? getSelection() : null
            if (sel && sel.toString().trim() !== '') return
        }
        if (menu.value && !menu.value.contains(e.target as Node) && clickFirst.value) {
            close()
        }
        if (!clickFirst.value) clickFirst.value = true
    }

    const open = () => {
        visible.value = true
        clickFirst.value = false
        if (isClient) window.addEventListener('click', closeDropdownByClickOutside)
    }

    const close = () => {
        visible.value = false
        if (isClient) window.removeEventListener('click', closeDropdownByClickOutside)
    }

    const changeState = () => {
        if (visible.value) close()
        else open()
    }

    onBeforeUnmount(() => {
        if (isClient) window.removeEventListener('click', closeDropdownByClickOutside)
    })

    return { clickFirst, closeDropdownByClickOutside, changeState, open, close }
}
